/**
 * Contact Information Detection & Sanitization Utility
 * 
 * Prevents tradespeople from sharing direct contact details in their profiles,
 * protecting the platform's monetization model.
 */

// Phone number patterns (UK focused)
const PHONE_PATTERNS = [
  /\b0[0-9]{10}\b/g,                           // 07123456789 or 02012345678
  /\b0[0-9]{2,4}[\s\-\.][0-9]{3,4}[\s\-\.][0-9]{3,4}\b/g, // 0712 345 6789
  /\+44\s?0?[0-9]{9,10}/g,                     // +447123456789, +44 7123456789, +4407123456789 (all +44 formats)
  /\bzero\s*seven[^\d]*[0-9]{9}\b/gi,          // "zero seven" followed by numbers
  /\b(oh|o)\s*seven[^\d]*[0-9]{9}\b/gi,        // "oh seven" followed by numbers
  /\b07\s*[0-9]{2,3}\s*[0-9]{2,3}\s*[0-9]{2,4}\b/g, // 07 xxx xxx xxx with spaces
];

// Email patterns
const EMAIL_PATTERNS = [
  /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/g,
  /\b[A-Za-z0-9._%+-]+\s*(at|@)\s*[A-Za-z0-9.-]+\s*(dot|\.)\s*(com|co\.uk|org|net|uk|io)\b/gi,
];

// URL/Website patterns
const URL_PATTERNS = [
  /\bhttps?:\/\/[^\s]+/gi,
  /\bwww\.[^\s]+/gi,
  /\b[a-z0-9-]+\.(com|co\.uk|org|net|io|uk|biz|info)\b/gi,
];

// Social media patterns
const SOCIAL_PATTERNS = [
  /\b(instagram|insta|ig)[\s:@]*[a-z0-9._]+\b/gi,
  /\b(facebook|fb)[\s:@\/]*[a-z0-9._]+\b/gi,
  /\b(twitter|x\.com)[\s:@\/]*[a-z0-9._]+\b/gi,
  /\b(tiktok)[\s:@\/]*[a-z0-9._]+\b/gi,
  /\b(snapchat|snap)[\s:@]*[a-z0-9._]+\b/gi,
  /\b(whatsapp|whats\s*app|wa\.me)[\s:@\/]*[0-9+\s]+\b/gi,
  /\b(telegram|t\.me)[\s:@\/]*[a-z0-9._]+\b/gi,
  /\b(linkedin)[\s:@\/]*[a-z0-9._-]+\b/gi,
];

// Contact instruction phrases
const CONTACT_PHRASES = [
  /\b(call|ring|phone|text|message|contact|reach|dm|pm)\s*(me|us)\s*(on|at|via|through)?\s*[^\s]*/gi,
  /\bmy\s*(number|phone|mobile|cell|contact|email|address)\s*(is|:)\s*[^\s]*/gi,
  /\b(get\s*in\s*touch|drop\s*me\s*a\s*(line|message)|hit\s*me\s*up)/gi,
  /\bwhatsapp\s*me\b/gi,
  /\bemail\s*me\s*(at)?\b/gi,
  /\bsend\s*(me\s*)?(a\s*)?(message|text|email)/gi,
];

/**
 * Checks if text contains any contact information
 * @param {string} text - The text to check
 * @returns {{hasContact: boolean, violations: string[]}} - Detection result
 */
export function detectContactInfo(text) {
  if (!text || typeof text !== 'string') {
    return { hasContact: false, violations: [] };
  }

  const violations = [];
  const normalizedText = text.toLowerCase();

  // Check phone patterns
  for (const pattern of PHONE_PATTERNS) {
    const matches = text.match(pattern);
    if (matches) {
      violations.push(`Phone number detected: ${matches[0]}`);
    }
  }

  // Check email patterns
  for (const pattern of EMAIL_PATTERNS) {
    const matches = text.match(pattern);
    if (matches) {
      violations.push(`Email address detected: ${matches[0]}`);
    }
  }

  // Check URL patterns
  for (const pattern of URL_PATTERNS) {
    const matches = text.match(pattern);
    if (matches) {
      violations.push(`Website/URL detected: ${matches[0]}`);
    }
  }

  // Check social media patterns
  for (const pattern of SOCIAL_PATTERNS) {
    const matches = text.match(pattern);
    if (matches) {
      violations.push(`Social media contact detected: ${matches[0]}`);
    }
  }

  // Check contact instruction phrases
  for (const pattern of CONTACT_PHRASES) {
    const matches = text.match(pattern);
    if (matches) {
      violations.push(`Contact instruction detected: ${matches[0]}`);
    }
  }

  return {
    hasContact: violations.length > 0,
    violations
  };
}

/**
 * Sanitizes text by removing/redacting contact information
 * @param {string} text - The text to sanitize
 * @returns {string} - Sanitized text
 */
export function sanitizeContactInfo(text) {
  if (!text || typeof text !== 'string') {
    return text;
  }

  let sanitized = text;

  // Replace phone numbers
  for (const pattern of PHONE_PATTERNS) {
    sanitized = sanitized.replace(pattern, '[phone number removed]');
  }

  // Replace emails
  for (const pattern of EMAIL_PATTERNS) {
    sanitized = sanitized.replace(pattern, '[email removed]');
  }

  // Replace URLs
  for (const pattern of URL_PATTERNS) {
    sanitized = sanitized.replace(pattern, '[link removed]');
  }

  // Replace social media handles
  for (const pattern of SOCIAL_PATTERNS) {
    sanitized = sanitized.replace(pattern, '[social contact removed]');
  }

  // Replace contact instruction phrases
  for (const pattern of CONTACT_PHRASES) {
    sanitized = sanitized.replace(pattern, '[contact details removed]');
  }

  // Clean up multiple consecutive [removed] tags
  sanitized = sanitized.replace(/\[[\w\s]+removed\]\s*\[[\w\s]+removed\]/g, '[contact details removed]');

  return sanitized;
}

/**
 * Validation message for users
 */
export const CONTACT_VALIDATION_MESSAGE = 
  "For safety and platform protection, please don't include phone numbers, email addresses, links, or social contact details in your profile. Use our secure messaging system to connect with employers.";

/**
 * Validates profile fields for contact information
 * @param {Object} profile - Profile object with bio, headline, etc.
 * @returns {{isValid: boolean, errors: Object}} - Validation result
 */
export function validateProfileFields(profile) {
  const errors = {};
  const fieldsToCheck = [
    { key: 'bio', label: 'Bio' },
    { key: 'headline', label: 'Headline' },
    { key: 'about', label: 'About' },
    { key: 'work_description', label: 'Work description' },
    { key: 'availability_notes', label: 'Availability notes' },
  ];

  for (const field of fieldsToCheck) {
    if (profile[field.key]) {
      const result = detectContactInfo(profile[field.key]);
      if (result.hasContact) {
        errors[field.key] = `${field.label} contains contact information that is not allowed.`;
      }
    }
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
}

export default {
  detectContactInfo,
  sanitizeContactInfo,
  validateProfileFields,
  CONTACT_VALIDATION_MESSAGE
};
